import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Icon } from '~/components/ui';
import { useLocale, useT } from '~/i18n';
import {
  fetchSearchFacets,
  searchJobs,
  type JobForYou,
  type ManualQuota,
  type SearchFacets,
  type SearchFilters,
  type SearchJob,
  type SearchProgram,
  type SearchSort,
  type SearchStart,
} from '../api/freeTierClient';
import { regionName } from '../lib/regions';
import { ApplyAction, JobRow, saveToggleFor } from './JobsForYouCard';

/**
 * El buscador de vacantes (2026-10-30), con o sin plan. Es la puerta gratis a
 * todas las vacantes que el envío del plan podría usar, y por eso «Vacantes para
 * ti» puede ir borrosa sin plan sin que el plan compre acceso (términos §8).
 *
 * Sin plan abre con una mezcla (nuevas, mejor pagadas, que empiezan pronto y
 * un barajado del día), para que haya algo que postular desde el primer
 * momento; con plan espera a que la persona busque o filtre. Los filtros solo
 * ofrecen lo que hay en su feed (`/jobs/search/facets`) y acotan dentro de él.
 */
interface Props {
  hasGmail: boolean;
  /** Postuladas en esta sesión. */
  applied: ReadonlySet<string>;
  onApply: (job: JobForYou) => void;
  /** Cargar la mezcla al abrir. */
  autoLoad: boolean;
  saved?: ReadonlySet<string>;
  onToggleSave?: (job: JobForYou) => void;
}

type Results =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ok'; q: string; jobs: SearchJob[]; page: number; hasMore: boolean; quota: ManualQuota };

const MIN_WAGES = [15, 18, 20, 25];
const STARTS: SearchStart[] = ['month', 'quarter', 'later'];
const SORTS: SearchSort[] = ['mix', 'newest', 'pay', 'soon'];
const NO_FILTERS: SearchFilters = {};

/** El ancla del buscador: «Prefiero buscar por mi cuenta» baja hasta acá. */
export const JOB_SEARCH_ID = 'buscar-vacantes';

/** Baja al buscador y deja el cursor en el campo. */
export function goToJobSearch() {
  const card = document.getElementById(JOB_SEARCH_ID);
  card?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  card?.querySelector('input')?.focus({ preventScroll: true });
}

function hasFilters(f: SearchFilters): boolean {
  return Boolean(f.program || f.region || f.minWage || f.start || f.housing || (f.sort && f.sort !== 'mix'));
}

export default function JobSearchCard({ hasGmail, applied, onApply, autoLoad, saved, onToggleSave }: Props) {
  const t = useT('workspace').dashboard.jobsForYou;
  const s = t.search;
  const tf = s.filters;
  const locale = useLocale();
  const [text, setText] = useState('');
  const [filters, setFilters] = useState<SearchFilters>(NO_FILTERS);
  const [facets, setFacets] = useState<SearchFacets | null>(null);
  const [results, setResults] = useState<Results>({ kind: 'idle' });
  const [loadingMore, setLoadingMore] = useState(false);
  /** Una búsqueda que vuelve tarde no pisa a la última. */
  const gen = useRef(0);

  const run = useCallback(async (q: string, f: SearchFilters) => {
    const mine = ++gen.current;
    setResults({ kind: 'loading' });
    const r = await searchJobs(q, 0, f);
    if (mine !== gen.current) return;
    setResults(r.status === 'ok' ? { kind: 'ok', q: q.trim(), ...r } : { kind: 'error' });
  }, []);

  useEffect(() => {
    if (autoLoad) void run('', NO_FILTERS);
  }, [autoLoad, run]);

  useEffect(() => {
    let alive = true;
    void fetchSearchFacets().then((f) => {
      if (alive) setFacets(f);
    });
    return () => {
      alive = false;
    };
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(text, filters);
  };

  /** Cambiar un filtro busca de una vez, con el texto del campo. */
  const change = (next: SearchFilters) => {
    setFilters(next);
    void run(text, next);
  };

  const more = async () => {
    if (results.kind !== 'ok' || !results.hasMore) return;
    const mine = gen.current;
    setLoadingMore(true);
    const r = await searchJobs(results.q, results.page + 1, filters);
    setLoadingMore(false);
    if (mine !== gen.current || r.status !== 'ok') return;
    setResults((prev) =>
      prev.kind === 'ok'
        ? { ...prev, jobs: [...prev.jobs, ...r.jobs.filter((j) => !prev.jobs.some((p) => p._id === j._id))], page: r.page, hasMore: r.hasMore }
        : prev,
    );
  };

  const programs = facets?.programs ?? [];
  const regions = facets?.regions ?? [];
  const selectClass = 'select select-bordered select-sm w-auto max-w-[15rem]';

  return (
    <div className="card rise" id={JOB_SEARCH_ID} style={{ scrollMarginTop: '5rem' }}>
      <div className="card-body space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="card-title">{s.title}</h2>
          {results.kind === 'ok' && <span className="text-xs hush">{t.quota(results.quota.used, results.quota.limit)}</span>}
        </div>
        <p className="text-sm hush">{s.lede}</p>

        <form className="flex gap-2 flex-wrap" role="search" onSubmit={submit}>
          <input
            type="search"
            className="input input-bordered flex-1 min-w-[12rem]"
            placeholder={s.placeholder}
            aria-label={s.title}
            value={text}
            maxLength={120}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="btn btn-primary gap-2" disabled={results.kind === 'loading'}>
            <Icon name="search" />
            {s.submit}
          </button>
        </form>

        <div className="flex gap-2 flex-wrap items-center">
          {programs.length > 1 && (
            <select
              className={selectClass}
              aria-label={tf.program}
              value={filters.program ?? ''}
              onChange={(e) => change({ ...filters, program: (e.target.value || undefined) as SearchProgram | undefined })}
            >
              <option value="">{tf.anyProgram}</option>
              {programs.map((p) => (
                <option key={p.key} value={p.key}>
                  {tf.programs[p.key] ?? p.key} ({p.count})
                </option>
              ))}
            </select>
          )}
          {regions.length > 1 && (
            <select
              className={selectClass}
              aria-label={tf.region}
              value={filters.region ?? ''}
              onChange={(e) => change({ ...filters, region: e.target.value || undefined })}
            >
              <option value="">{tf.anyRegion}</option>
              {regions.map((r) => (
                <option key={r.key} value={r.key}>
                  {regionName(r.key, locale)} ({r.count})
                </option>
              ))}
            </select>
          )}
          <select
            className={selectClass}
            aria-label={tf.wage}
            value={filters.minWage ?? ''}
            onChange={(e) => change({ ...filters, minWage: e.target.value ? Number(e.target.value) : undefined })}
          >
            <option value="">{tf.anyWage}</option>
            {MIN_WAGES.map((n) => (
              <option key={n} value={n}>
                {tf.wageAtLeast(n)}
              </option>
            ))}
          </select>
          <select
            className={selectClass}
            aria-label={tf.start}
            value={filters.start ?? ''}
            onChange={(e) => change({ ...filters, start: (e.target.value || undefined) as SearchStart | undefined })}
          >
            <option value="">{tf.anyStart}</option>
            {STARTS.map((k) => (
              <option key={k} value={k}>
                {tf.starts[k]}
              </option>
            ))}
          </select>
          {/* Con texto manda la relevancia: el orden solo aplica sin texto. */}
          {!text.trim() && (
            <select
              className={selectClass}
              aria-label={tf.sort}
              value={filters.sort ?? 'mix'}
              onChange={(e) => change({ ...filters, sort: e.target.value as SearchSort })}
            >
              {SORTS.map((k) => (
                <option key={k} value={k}>
                  {tf.sorts[k]}
                </option>
              ))}
            </select>
          )}
          {(facets?.housing ?? 0) > 0 && (
            <label className="label cursor-pointer gap-2 text-sm">
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary"
                checked={filters.housing === true}
                onChange={(e) => change({ ...filters, housing: e.target.checked || undefined })}
              />
              {tf.housing}
            </label>
          )}
          {hasFilters(filters) && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => change(NO_FILTERS)}>
              {tf.clear}
            </button>
          )}
        </div>

        {results.kind === 'loading' && (
          <p className="text-sm hush flex items-center gap-2">
            <span className="loading loading-spinner loading-xs" />
            {s.loading}
          </p>
        )}
        {results.kind === 'error' && (
          <p className="text-sm" style={{ color: 'var(--coral-ink)' }}>
            {s.failed}
          </p>
        )}
        {results.kind === 'ok' && (
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">{results.q ? s.results(results.q) : s[filters.sort ?? 'mix']}</h3>
            {results.jobs.length === 0 ? (
              <p className="text-sm hush">{s.empty}</p>
            ) : (
              results.jobs.map((job) => {
                const today = applied.has(job._id);
                return (
                  <JobRow
                    key={job._id}
                    job={job}
                    save={saveToggleFor(job, saved, onToggleSave)}
                    action={
                      <ApplyAction
                        job={job}
                        isApplied={today || job.applied}
                        appliedLabel={today ? t.applied : s.alreadyApplied}
                        hasGmail={hasGmail}
                        onApply={onApply}
                      />
                    }
                  />
                );
              })
            )}
            {results.hasMore && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void more()} disabled={loadingMore}>
                {loadingMore && <span className="loading loading-spinner loading-xs" />}
                {s.more}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
